import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, { useState, useEffect } from 'react';
import { defaultCreateUpdateCustomerVehicleInsurance, IAppCustomerVehicleInsurance, ICreateUpdateCustomerVehicleInsurance } from '../../../Interfaces/Customer/IAppCustomerVehicleInsurance';
import { AlertDialog, hasValue, PermissionSet, VehicleInsuranceCard, VehicleInsuranceInformation } from '@amsanalytics/react-shared-library';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Input, InputChangeEvent, RadioGroup, RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { authService } from '../../../index';
import { isNil } from 'ramda';
import { showErrorMessage } from '../../../store/application/actions';
import { httpDeleteAsync, httpGetAsync, httpPostAsync, httpPutAsync } from '../../../utils/HttpClient';
import { useDispatch, useSelector } from 'react-redux';
import styles from './_customer-insurance.module.scss';
import { IInsuranceVerification } from '../../../Interfaces/Customer/IInsuranceVerification';
import { useConfiguration } from '../../../Data/ConfigurationData';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { AppState } from '../../../store/rootReducer';
import { useDialog } from '../../../providers/DialogProvider';
import { InsuranceFormWindow } from './InsuranceFormWindow';
import { AmsInsuranceAgent } from '../../../Interfaces/Insurance/AmsInsuranceAgent';
import ConfirmationDialog from '../../Dialogs/ConfirmationDialog';
import { envelopeIcon, uploadIcon } from '@progress/kendo-svg-icons';
import { sendDocUploadEmailUtil, sendDocUploadTextUtil } from '../../../utils/portalLinkUtils';
import { IEmployee } from '../../../Interfaces/Admin/IEmployee';
import { ILoggedInEmployee } from '../../../Interfaces/Admin/ILoggedInEmployee';
import { permissions } from '../../../utils/constants';
import { userHasPermission } from '../../../utils/utils';
import { getSelectedFinanceQuote } from '../../../utils/quoteUtils';
import { PaymentFrequency } from '@amsanalytics/react-shared-library';

export interface CustomerVehicleInsuranceTabProps {
  applicationId: string;
  customerId: string;
  appCustomerId: string; // ID of the AppCustomer
  customerVehicleInsurances: IAppCustomerVehicleInsurance[]; // List of customer vehicle insurances
  onCreate: (customerVehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => void; // Fires after user adds a new item
  onEdit: (customerVehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => void; // Fires after user edits an existing item
  onDelete: (customerVehicleInsuranceId: string) => void; // Fires after user deletes an existing item
}

// Validation interfaces
interface ValidationError {
  customerName: string;
  error: string;
}

// Helper function to convert IInsuranceVerification to VehicleInsuranceInformation
// If the Modives VIN matches the application vehicle VIN, auto-populate stock number
const convertPendingInsuranceToVehicleInsurance = (pendingInsurance: IInsuranceVerification, applicationVehicle?: { vin?: string; stockNumber?: string }): VehicleInsuranceInformation => {
  const vinMatches = pendingInsurance.vin
    && applicationVehicle?.vin
    && pendingInsurance.vin.toUpperCase().trim() === applicationVehicle.vin.toUpperCase().trim();

  return {
    ...defaultCreateUpdateCustomerVehicleInsurance,
    insuranceCompany: pendingInsurance.carrierName,
    policyNumber: pendingInsurance.policyNumber,
    effectiveDate: pendingInsurance.policyStartDate ? new Date(pendingInsurance.policyStartDate) : null,
    expirationDate: pendingInsurance.policyEndDate ? new Date(pendingInsurance.policyEndDate) : null,
    comprehensiveDeductible: pendingInsurance.comprehensiveDeductible,
    collisionDeductible: pendingInsurance.collisionDeductible,
    insuranceVerificationRequestId: pendingInsurance.insuranceVerificationRequestId,
    stockNumber: vinMatches ? applicationVehicle.stockNumber ?? '' : '',
  };
};

// Validation helper functions
const containsNumbers = (str: string): boolean => {
  return /\d/.test(str);
};

const validatePhoneNumber = (phoneNumber: string): { isValid: boolean; error?: string } => {
  // Remove formatting characters
  const cleanPhone = phoneNumber.replace(/[()\-\s]/g, '');
  
  // Check if it's exactly 10 digits
  if (!/^\d{10}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone number must be exactly 10 digits' };
  }
  
  // Check if all digits are the same
  if (/^(\d)\1{9}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone number cannot have all identical digits' };
  }
  
  return { isValid: true };
};

const validateCustomerData = (customers: any[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  customers.forEach(appCustomer => {
    const customer = appCustomer.customer;
    const customerName = `${customer.firstName} ${customer.middleName || ''} ${customer.lastName}`.trim();
    
    // Validate names don't contain numbers
    if (containsNumbers(customer.firstName)) {
      errors.push({
        customerName,
        error: 'First name cannot contain numbers'
      });
    }
    
    if (customer.middleName && containsNumbers(customer.middleName)) {
      errors.push({
        customerName,
        error: 'Middle name cannot contain numbers'
      });
    }
    
    if (containsNumbers(customer.lastName)) {
      errors.push({
        customerName,
        error: 'Last name cannot contain numbers'
      });
    }
    
    // Validate phone numbers
    if (customer.customerPhone && customer.customerPhone.length > 0) {
      customer.customerPhone.forEach((phone: any) => {
        const phoneValidation = validatePhoneNumber(phone.phoneNumber);
        if (!phoneValidation.isValid) {
          errors.push({
            customerName,
            error: `Phone number (${phone.phoneNumber}): ${phoneValidation.error}`
          });
        }
      });
    }
  });
  
  return errors;
};

export default function CustomerVehicleInsuranceTab(props: CustomerVehicleInsuranceTabProps) {
  const dispatch = useDispatch();
  const { data: configuration } = useConfiguration();
  // Is creating a new insurance item
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  // ID of the item being edited
  const [editItemId, setEditItemId] = useState<string | null>(null);
  // ID of the item being deleted. We'll prompt the user to confirm deletion
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  // Add state for verification status
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  // State for pending insurance data
  const [pendingInsurance, setPendingInsurance] = useState<IInsuranceVerification | null>(null);
  const [showPendingInsuranceEditor, setShowPendingInsuranceEditor] = useState<boolean>(false);
  // Add state for verification results link and iframe visibility
  const [verificationResultLink, setVerificationResultLink] = useState<string | null>(null);
  const [showVerificationIframe, setShowVerificationIframe] = useState<boolean>(false);
  // Add state for validation errors dialog
  const [openDialog, closeDialog] = useDialog();
  // Add state for applicant selection dialog (when co-buyer exists)
  const [showApplicantSelectionDialog, setShowApplicantSelectionDialog] = useState<boolean>(false);
  const [isGeneratingDocUploadLink, setIsGeneratingDocUploadLink] = useState<boolean>(false);
  const [pendingDocUploadAfterVerification, setPendingDocUploadAfterVerification] = useState<boolean>(false);
  const [docUploadCustomerId, setDocUploadCustomerId] = useState<string | null>(null);
  // Which action finishes the shared document-upload flow: email/text send dialog or in-page iframe
  const [docUploadMode, setDocUploadMode] = useState<'send' | 'iframe'>('send');
  const [uploadIframeLink, setUploadIframeLink] = useState<string | null>(null);
  const [showUploadIframe, setShowUploadIframe] = useState<boolean>(false);
  // Document upload send dialog state
  const [showDocUploadSendDialog, setShowDocUploadSendDialog] = useState<boolean>(false);
  const [docUploadSendMethod, setDocUploadSendMethod] = useState<'email' | 'text'>('email');
  const [docUploadEmail, setDocUploadEmail] = useState<string>('');
  const [docUploadPhone, setDocUploadPhone] = useState<string>('');
  const [docUploadMagicLink, setDocUploadMagicLink] = useState<string>('');
  // Insurance agents for the Select Insurance/Agent dialog
  const [insuranceAgents, setInsuranceAgents] = useState<AmsInsuranceAgent[]>([]);
  
  const currentApp: IApplication = useSelector(
    (state: AppState) => state.applications.editingApp!
  );

  const activeUser: ILoggedInEmployee | undefined = useSelector(
    (state: AppState) => state.applications.loggedInUser?.employee
  );

  const currentEmployee: IEmployee | undefined = useSelector(
    (state: AppState) => {
      const employees = state.applications.employees;
      const activeUserId = state.applications.loggedInUser?.employee?.id;
      return activeUserId ? employees?.find((e: IEmployee) => e.id === activeUserId) : undefined;
    }
  );

  const amsCpiConfigs = useSelector(
    (state: AppState) => state.applications.lookups.cpiConfigs
  );

  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);

  // Fresh CPI config fetched from DbSync when the dialog opens
  const [freshCpiConfigs, setFreshCpiConfigs] = useState<{ paymentFrequency: number; factorAmount: number }[]>([]);

  const defaultCpiFactor = (freshCpiConfigs.length > 0 ? freshCpiConfigs : amsCpiConfigs).find(
    (c: { paymentFrequency: number; factorAmount: number }) => c.paymentFrequency === selectedFinanceQuote?.paymentFrequency
  )?.factorAmount ?? 0;

  const getNextAccrueDate = (startDate: Date, frequency: PaymentFrequency): Date => {
    const next = new Date(startDate);
    switch (frequency) {
      case PaymentFrequency.Weekly: next.setDate(next.getDate() + 7); break;
      case PaymentFrequency.Biweekly: next.setDate(next.getDate() + 14); break;
      case PaymentFrequency.FourWeekly: next.setDate(next.getDate() + 28); break;
      case PaymentFrequency.Semimonthly: next.setDate(next.getDate() + 15); break;
      case PaymentFrequency.Monthly: next.setMonth(next.getMonth() + 1); break;
      case PaymentFrequency.Bimonthly: next.setMonth(next.getMonth() + 2); break;
      case PaymentFrequency.Quarterly: next.setMonth(next.getMonth() + 3); break;
      case PaymentFrequency.FourMonthly: next.setMonth(next.getMonth() + 4); break;
      case PaymentFrequency.Semiannual: next.setMonth(next.getMonth() + 6); break;
      case PaymentFrequency.Annual: next.setFullYear(next.getFullYear() + 1); break;
      default: next.setMonth(next.getMonth() + 1); break;
    }
    return next;
  };

  const defaultCpiNextAccrueDate = selectedFinanceQuote
    ? getNextAccrueDate(
        currentApp?.applicationFunding?.firstDueDate
          ? new Date(currentApp.applicationFunding.firstDueDate)
          : new Date(),
        selectedFinanceQuote.paymentFrequency)
    : null;

  const payFrequencyLabelMap: Record<number, string> = {
    [PaymentFrequency.Weekly]: 'Weekly',
    [PaymentFrequency.Biweekly]: 'Bi-weekly',
    [PaymentFrequency.Semimonthly]: 'Semi-monthly',
    [PaymentFrequency.Monthly]: 'Monthly',
    [PaymentFrequency.FourWeekly]: 'Every 4 Weeks',
    [PaymentFrequency.Bimonthly]: 'Bi-monthly',
    [PaymentFrequency.Quarterly]: 'Quarterly',
    [PaymentFrequency.Semiannual]: 'Semi-annual',
    [PaymentFrequency.Annual]: 'Annual',
    [PaymentFrequency.FourMonthly]: 'Every 4 Months',
  };

  const loanPayFrequencyLabel = selectedFinanceQuote
    ? payFrequencyLabelMap[selectedFinanceQuote.paymentFrequency] ?? ''
    : '';

  // Load insurance agents and CPI config on mount
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const bearerToken = await authService.getToken();
        const response = await httpGetAsync('api/customerVehicleInsurance/agents', bearerToken!);
        if (response) {
          const data = await response.json();
          setInsuranceAgents(data.insuranceAgents || []);
        }
      } catch (error) {
        console.error('Failed to load insurance agents:', error);
      }
    };
    loadAgents();
    loadCpiConfig();
  }, []);

  const loadCpiConfig = async () => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync('api/customerVehicleInsurance/cpiConfig', bearerToken!);
      if (response) {
        const data = await response.json();
        setFreshCpiConfigs(data || []);
      }
    } catch (error) {
      console.error('Failed to load CPI config from DbSync:', error);
    }
  };

  // Async post method to create a new insurance item
  const createItemAsync = async (item: ICreateUpdateCustomerVehicleInsurance) => {
    // get token
    const bearerToken = await authService.getToken();
    const appCustomerVehicleInsurance: Response | undefined = await httpPostAsync(
      `api/customerVehicleInsurance`,
      item,
      bearerToken!
    );
    if (isNil(appCustomerVehicleInsurance)) {
      dispatch(showErrorMessage(`Failed to create insurance item`));
      return;
    }
    return await appCustomerVehicleInsurance.json();
  };

  // User has added a new insurance item
  const handleCreate = async (vehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => {
    const createdItem = await createItemAsync({ ...vehicleInsurance, appCustomerId: props.appCustomerId });
    if (!createdItem || createdItem.hasOwnProperty('errors')) {
      setIsCreating(false);
      return;
    }
    props.onCreate({ ...vehicleInsurance, id: createdItem.appCustomerVehicleInsuranceId, appCustomerId: props.appCustomerId });
    setIsCreating(false);
    // Clear pending insurance after successful creation
    setPendingInsurance(null);
  };

  // Handle create from pending insurance
  const handleCreateFromPending = async (vehicleInsurance: VehicleInsuranceInformation) => {
    const createdItem = await createItemAsync({ ...vehicleInsurance, appCustomerId: props.appCustomerId });
    if (!createdItem || createdItem.hasOwnProperty('errors')) {
      return;
    }
    props.onCreate({ ...vehicleInsurance, id: createdItem.appCustomerVehicleInsuranceId, appCustomerId: props.appCustomerId });
    setShowPendingInsuranceEditor(false);
    setPendingInsurance(null); // Only clear when actually saved
  };

  const cancelExistingVerificationAsync = async (): Promise<boolean> => {
    const bearerToken = await authService.getToken();
    const response: Response | undefined = await httpDeleteAsync(
      `api/insuranceVerification/cancelVerification/${props.applicationId}`,
      bearerToken!
    );
    if (isNil(response)) {
      dispatch(showErrorMessage(`Failed to cancel existing verification`));
      return false;
    }
    return true;
  };

  const verifyInsuranceAsync = async (customerId: string) => {
    // get token
    const bearerToken = await authService.getToken();
    const appCustomerVehicleInsurance: Response | undefined = await httpGetAsync(
      `api/insuranceVerification/sendVerificationLinkEmail/${props.applicationId}/${customerId}`,
      bearerToken!
    );
    if (isNil(appCustomerVehicleInsurance)) {
      dispatch(showErrorMessage(`Failed to verify insurance`));
      return;
    }
    return await appCustomerVehicleInsurance.json();
  };

  const getVerificationResultsAsync = async () => {
    // get token
    const bearerToken = await authService.getToken();
    const verificationResultLink: Response | undefined = await httpGetAsync(
      `api/insuranceVerification/getVerificationResultsLinkByApplicationId/${props.applicationId}`,
      bearerToken!
    );
    if (isNil(verificationResultLink)) {
      dispatch(showErrorMessage(`Failed to get verification results`));
      return;
    }
    return await verificationResultLink.json();
  };

  const getPendingInsuranceAsync = async () => {
    // This returns an object of type IInsuranceVerification
    // Now uses applicationId to get the latest complete insurance across all applicants
    const bearerToken = await authService.getToken();
    const pendingVehicleInsurance: Response | undefined = await httpGetAsync(
      `api/insuranceVerification/getLatestCompleteInsuranceDetail/${props.applicationId}`,
      bearerToken!
    );
    if (isNil(pendingVehicleInsurance)) {
      dispatch(showErrorMessage(`Failed to get new insurance`));
      return;
    }
    return await pendingVehicleInsurance.json();
  };

  // Check if the application has a co-buyer (more than one applicant)
  const hasCoBuyer = (): boolean => {
    return (currentApp?.appCustomers?.length ?? 0) > 1;
  };

  // Get the primary applicant's customer ID
  const getPrimaryApplicantCustomerId = (): string => {
    const primaryApplicant = currentApp?.appCustomers?.find(ac => ac.customerRole?.isPrimaryApplicant);
    return primaryApplicant?.customerId ?? props.customerId;
  };

  // Send verification for a specific customer
  const sendVerificationForCustomer = async (customerId: string) => {
    setIsVerifying(true);
    setShowApplicantSelectionDialog(false);

    const createdItem = await verifyInsuranceAsync(customerId);
    if (!createdItem || createdItem.hasOwnProperty('errors')) {
      setIsVerifying(false);
      return;
    }
    
    // After sending verification email, check the status
    await loadVerificationStatus();
    setIsVerifying(false);
  };

  // Proceed with sending the verification (after all checks pass)
  const proceedWithVerification = async () => {
    // Check if there's a co-buyer - if so, show selection dialog
    if (hasCoBuyer()) {
      setShowApplicantSelectionDialog(true);
      return;
    }
    
    // No co-buyer, proceed with primary applicant
    await sendVerificationForCustomer(getPrimaryApplicantCustomerId());
  };

  // Request the verification with validation
  const handleVerify = async () => {
    // Validate customer data before proceeding (guard against null/undefined currentApp or appCustomers)
    const errors = currentApp?.appCustomers
      ? validateCustomerData(currentApp.appCustomers)
      : [];
    
    if (errors.length > 0) {
      const errorMessage = errors
          .map(error => `• ${error.error}`)
          .join('</br>');

      openDialog({
        children: (
          <AlertDialog
            title="Validation Errors"
            message={errorMessage}
            onCancel={closeDialog}
          />
        ),
      });
      return;
    }

    // If a verification already exists, warn the user before resending
    if (verificationStatus && verificationStatus !== '') {
      openDialog({
        children: (
          <ConfirmationDialog
            closeDialog={closeDialog}
            title="Resend Verification Email"
            prompt="If you send the customer another verification email, the current verification will be deleted. Do you want to continue?"
            accept={async () => {
              closeDialog();
              const cancelled = await cancelExistingVerificationAsync();
              if (cancelled) {
                await proceedWithVerification();
              }
            }}
          />
        ),
      });
      return;
    }
    
    await proceedWithVerification();
  };

  // "Send Document Link" button clicked directly
  const handleSendDocumentLinkClick = async () => {
    setDocUploadMode('send');
    // If no verification exists, send one first then proceed to the upload dialog
    if (!verificationStatus || verificationStatus === '') {
      if (hasCoBuyer()) {
        setPendingDocUploadAfterVerification(true);
        setShowApplicantSelectionDialog(true);
        return;
      }
      setPendingDocUploadAfterVerification(true);
      await sendVerificationForCustomer(getPrimaryApplicantCustomerId());
      return;
    }

    // Verification exists: if co-buyers, let user pick which customer to generate the link for
    if (hasCoBuyer()) {
      setDocUploadCustomerId(null);
      setShowApplicantSelectionDialog(true);
      return;
    }
    await proceedToDocumentUploadDialog(getPrimaryApplicantCustomerId());
  };

  // After verification completes, continue to the upload dialog if that's what triggered it
  useEffect(() => {
    if (pendingDocUploadAfterVerification && verificationStatus && verificationStatus !== '') {
      setPendingDocUploadAfterVerification(false);
      // Use the customer who was just verified (stored in docUploadCustomerId), or fall back to primary
      proceedToDocumentUpload(docUploadCustomerId ?? getPrimaryApplicantCustomerId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStatus, pendingDocUploadAfterVerification]);

  // "Upload Documents" button clicked: same preconditions as Send Document Link, but finishes in an iframe
  const handleUploadDocumentsClick = async () => {
    setDocUploadMode('iframe');
    setDocUploadCustomerId(null);
    // If no verification exists, send one first then continue
    if (!verificationStatus || verificationStatus === '') {
      setPendingDocUploadAfterVerification(true);
      if (hasCoBuyer()) {
        setShowApplicantSelectionDialog(true);
        return;
      }
      await sendVerificationForCustomer(getPrimaryApplicantCustomerId());
      return;
    }
    if (hasCoBuyer()) {
      setShowApplicantSelectionDialog(true);
      return;
    }
    // Immediate path: call the iframe flow directly. `docUploadMode` set above is not visible to
    // this closure until the next render, so routing through `proceedToDocumentUpload` here would
    // read the PREVIOUS mode and open the send dialog on the first click. The mode state is only
    // needed by the deferred paths (applicant selection, send-verification-first), which run after
    // a re-render.
    await openDocumentUploadIframe(getPrimaryApplicantCustomerId());
  };

  // Finish the document upload flow according to the mode chosen by the button clicked
  const proceedToDocumentUpload = async (customerId: string) => {
    if (docUploadMode === 'iframe') {
      await openDocumentUploadIframe(customerId);
    } else {
      await proceedToDocumentUploadDialog(customerId);
    }
  };

  // Fetch the Modives document upload magic link; returns null (after showing an error) on failure
  const fetchDocumentUploadMagicLink = async (customerId: string): Promise<string | null> => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync(
        `api/insuranceVerification/documentUploadMagicLink/${props.applicationId}/${customerId}`,
        bearerToken!
      );

      if (isNil(response)) {
        dispatch(showErrorMessage('Failed to generate document upload link'));
        return null;
      }

      const data = await response.json();
      if (!data?.magicLink) {
        dispatch(showErrorMessage(data?.detail || 'Failed to generate document upload link'));
        return null;
      }
      return data.magicLink as string;
    } catch (error: any) {
      dispatch(showErrorMessage(error?.message || 'Failed to generate document upload link'));
      return null;
    }
  };

  const openDocumentUploadIframe = async (customerId: string) => {
    setIsGeneratingDocUploadLink(true);
    const magicLink = await fetchDocumentUploadMagicLink(customerId);
    setIsGeneratingDocUploadLink(false);
    if (!magicLink) {
      return;
    }
    setUploadIframeLink(magicLink);
    setShowVerificationIframe(false);
    setShowUploadIframe(true);
  };

  const proceedToDocumentUploadDialog = async (customerId: string) => {
    setIsGeneratingDocUploadLink(true);

    try {
      const magicLink = await fetchDocumentUploadMagicLink(customerId);
      if (!magicLink) {
        setIsGeneratingDocUploadLink(false);
        return;
      }
      const data = { magicLink };

      // Default email/phone from primary applicant
      const primaryAppCustomer = currentApp?.appCustomers?.find(
        ac => ac.customerId === customerId
      );
      const defaultEmail = primaryAppCustomer?.customer?.customerEmail
        ?.find((e: any) => !e.doNotEmail)?.emailAddress || '';
      const defaultPhone = primaryAppCustomer?.customer?.customerPhone
        ?.find((p: any) => !p.doNotText && ['Mobile', 'Cell'].includes(p.type))?.phoneNumber || '';

      setDocUploadMagicLink(data.magicLink);
      setDocUploadEmail(defaultEmail);
      setDocUploadPhone(defaultPhone);
      setDocUploadSendMethod(defaultEmail ? 'email' : 'text');
      setIsGeneratingDocUploadLink(false);
      setShowDocUploadSendDialog(true);
    } catch (error: any) {
      dispatch(showErrorMessage(error?.message || 'Failed to generate document upload link'));
      setIsGeneratingDocUploadLink(false);
    }
  };

  const handleDocUploadSend = async () => {
    setShowDocUploadSendDialog(false);

    if (docUploadSendMethod === 'email' && hasValue(docUploadEmail)) {
      const emailBody = `<p>Please use the link below to upload your insurance documents:</p><p><a href="${docUploadMagicLink}">${docUploadMagicLink}</a></p>`;
      sendDocUploadEmailUtil(
        currentApp!,
        { subject: 'Insurance Document Upload', body: emailBody, emailAddresses: [docUploadEmail] },
        dispatch,
        () => {}
      );
    } else if (docUploadSendMethod === 'text' && hasValue(docUploadPhone)) {
      const textBody = `Please use this link to upload your insurance documents: ${docUploadMagicLink}`;
      sendDocUploadTextUtil(
        currentApp!,
        { message: textBody, phoneNumbers: [docUploadPhone] },
        currentEmployee,
        dispatch,
        () => {}
      );
    }
  };

  

  const getVerificationResultsLink = async () => {
    // If iframe is currently showing, just hide it
    if (showVerificationIframe) {
      setShowVerificationIframe(false);
      return;
    }

    // Only one iframe panel at a time
    setShowUploadIframe(false);
    setIsVerifying(true);

    const response = await getVerificationResultsAsync();
    if (!response || response.hasOwnProperty('errors')) {
      setIsVerifying(false);
      return;
    }
    
    // Set the verification result link and show the iframe
    if (response.verificationResultLink) {
      setVerificationResultLink(response.verificationResultLink);
      setShowVerificationIframe(true);
    }
    
    // After getting verification results, check the status
    await loadVerificationStatus();
    setIsVerifying(false);
  };

  const getVerificationStatusAsync = async () => {
    // get token
    const bearerToken = await authService.getToken();
    const status: Response | undefined = await httpGetAsync(
      `api/insuranceVerification/getLosVerificationStatus/${props.applicationId}`,
      bearerToken!
    );
    if (isNil(status)) {
      dispatch(showErrorMessage(`Failed to verify insurance`));
      return;
    }
    return await status.json();
  };

  // Load verification status and update state
  // Server now uses applicationId to find the most recent verification across all customers
  const loadVerificationStatus = async () => {
    try {
      const statusResponse = await getVerificationStatusAsync();
      
      if (statusResponse && statusResponse.status && statusResponse.status !== '') {
        setVerificationStatus(statusResponse.status);

        // Always refresh the verification results link when status is Complete
        // This ensures co-applicant results are fetched even if the primary applicant's
        // results were already loaded and the iframe is already showing
        if (typeof statusResponse.status === 'string' && statusResponse.status.includes('Complete')) {
          const response = await getVerificationResultsAsync();
          if (response && response.verificationResultLink) {
            setVerificationResultLink(response.verificationResultLink);
            setShowVerificationIframe(true);
          }
        }
      } else {
        setVerificationStatus(null);
      }
    } catch (error) {
      setVerificationStatus(null);
    }
  };

  // Load pending insurance on component mount
  // Server now uses applicationId to find the most recent Complete verification across all customers
  const loadPendingInsurance = async () => {
    try {
      const pendingInsuranceData = await getPendingInsuranceAsync();
      
      // Valid response should have carrierName and no error indicators
      if (pendingInsuranceData && 
          pendingInsuranceData.carrierName !== null && 
          pendingInsuranceData.hasError !== true &&
          !pendingInsuranceData.hasOwnProperty('status')) {
        setPendingInsurance(pendingInsuranceData);
        setShowPendingInsuranceEditor(true);

      }
    } catch (error) {
      console.error('Failed to load pending insurance:', error);
    }
  };

  // Load verification status and pending insurance on component mount
  // Only call these if CheckMyDriver is enabled for the client
  useEffect(() => {
    if (configuration?.featureFlags[FeatureFlagIds.UseCheckMyDriver] === true) {
      loadVerificationStatus();
      loadPendingInsurance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customerId, configuration?.featureFlags[FeatureFlagIds.UseCheckMyDriver]]);

  // Sync insurance stock number to match the vehicle stock number on the application
  useEffect(() => {
    const vehicleStockNumber = currentApp?.vehicle?.stockNumber;
    if (!vehicleStockNumber) return;

    for (const insurance of props.customerVehicleInsurances) {
      if (insurance.stockNumber?.trim().toUpperCase() !== vehicleStockNumber.trim().toUpperCase()) {
        const updated: ICreateUpdateCustomerVehicleInsurance = {
          ...insurance,
          appCustomerId: props.appCustomerId,
          stockNumber: vehicleStockNumber,
        };
        updateItemAsync(updated).then((result) => {
          if (result && !result.hasOwnProperty('errors')) {
            props.onEdit(updated);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApp?.vehicle?.stockNumber, props.customerVehicleInsurances.length]);

  // Compute button text based on verification status
  const getButtonText = () => {
    if (verificationStatus && verificationStatus !== '') {
      return `Verification ${verificationStatus}`;
    }
    return 'Send Verify Insurance Email';
  };

  const getResultsButtonText = () => {
    if (showVerificationIframe) {
      return 'Hide Verification Results';
    }
    if (verificationStatus && typeof verificationStatus === 'string' && verificationStatus.includes('Complete')) {
      return `View Verification Results`;
    }
    return 'Verification Results Not Available';
  };

  const getResultsButtonIcon = () => {
    if (showVerificationIframe) {
      return 'minus';
    }
    return 'eye';
  };

  // Async put method to update an existing insurance item
  const updateItemAsync = async (item: ICreateUpdateCustomerVehicleInsurance) => {
    // get token
    const bearerToken = await authService.getToken();
    const appCustomerVehicleInsurance: Response | undefined = await httpPutAsync(
      `api/customerVehicleInsurance`,
      item,
      bearerToken!
    );
    if (isNil(appCustomerVehicleInsurance)) {
      dispatch(showErrorMessage(`Failed to update insurance item`));
      return;
    }
    return await appCustomerVehicleInsurance.json();
  };

  // User has edited an existing insurance item
  const handleEdit = async (customerVehicleInsurance: ICreateUpdateCustomerVehicleInsurance) => {
    const updatedItem = await updateItemAsync({ ...customerVehicleInsurance, appCustomerId: props.appCustomerId });
    if (!updatedItem || updatedItem.hasOwnProperty('errors')) {
      setEditItemId(null);
      return;
    }
    props.onEdit(customerVehicleInsurance);
    setEditItemId(null);
  };

  // Async delete method to delete an existing insurance item
  const deleteItemAsync = async () => {
    // get token
    const bearerToken = await authService.getToken();
    const deletedId: Response | undefined = await httpDeleteAsync(
      `api/customerVehicleInsurance/${deleteItemId}`,
      bearerToken!
    );
    if (isNil(deletedId)) {
      dispatch(showErrorMessage(`Failed to delete insurance item`));
      return;
    }
    return await deletedId.json();
  };

  // User has deleted an existing insurance item
  const handleDelete = async () => {
    const deletedId = await deleteItemAsync();
    if (isNil(deletedId)) {
      return;
    }
    props.onDelete(deleteItemId!);
    setDeleteItemId(null);
  };

  return (
    <div className={styles.customerInsuranceContainer}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {configuration?.featureFlags[FeatureFlagIds.UseCheckMyDriver] === true && (
        <>
          <Tooltip anchorElement="target" position="bottom">
            <Button
              onClick={() => handleVerify()}
              disabled={isVerifying || isGeneratingDocUploadLink || hasValue(editItemId)}
              themeColor={'primary'}
              svgIcon={envelopeIcon}
              title="Send the customer a login link to verify their insurance via Modives"
              style={{ flexShrink: 0 }}>
              {getButtonText()}
            </Button>
          </Tooltip>
          {activeUser && userHasPermission(activeUser, permissions.checkMyDriverDocumentUpload, PermissionSet.R) && (
          <Tooltip anchorElement="target" position="bottom">
            <Button
              onClick={() => handleSendDocumentLinkClick()}
              disabled={isVerifying || isGeneratingDocUploadLink || hasValue(editItemId)}
              themeColor={'primary'}
              svgIcon={uploadIcon}
              title="Text or email the customer a secure link to upload insurance documents"
              style={{ flexShrink: 0 }}>
              Send Document Link
            </Button>
          </Tooltip>
          )}
          {activeUser && userHasPermission(activeUser, permissions.checkMyDriverDocumentUpload, PermissionSet.R) && (
          <Tooltip anchorElement="target" position="bottom">
            <Button
              onClick={() => handleUploadDocumentsClick()}
              disabled={isVerifying || isGeneratingDocUploadLink || hasValue(editItemId)}
              themeColor={'primary'}
              svgIcon={uploadIcon}
              title="Upload insurance documents for the customer in a window"
              style={{ flexShrink: 0 }}>
              Upload Documents
            </Button>
          </Tooltip>
          )}
          <Button
            onClick={() => getVerificationResultsLink()}
            disabled={
              isVerifying || 
              hasValue(editItemId) || 
              (!verificationStatus || typeof verificationStatus !== 'string' || !verificationStatus.includes('Complete'))
            }
            themeColor={'primary'}
            icon={getResultsButtonIcon()}
            style={{ flexShrink: 0 }}>
            {getResultsButtonText()}
          </Button>
        </>
        )}
        <Button
          onClick={() => { loadCpiConfig(); setIsCreating(true); }}
          disabled={isCreating || hasValue(editItemId) || props.customerVehicleInsurances.length > 0}
          themeColor={'primary'}
          icon='plus'
          style={{ flexShrink: 0 }}>
          Add Insurance
        </Button>
      </div>

      {/* Side-by-side layout: Verification Results on the left, cards/editors on the right */}
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'flex-start'
      }}>
        {/* Left side - Upload Documents iFrame (staff upload on the customer's behalf) */}
        {showUploadIframe && uploadIframeLink && (
          <div style={{
            width: '600px',
            flexShrink: 0,
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px' }}>
              <h3 style={{ margin: 0 }}>Upload Documents</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  onClick={() => window.open(uploadIframeLink, '_blank', 'noopener,noreferrer')}
                  themeColor={'secondary'}
                  size='small'>
                  Open in a new tab
                </Button>
                <Button
                  onClick={() => setShowUploadIframe(false)}
                  themeColor={'secondary'}
                  icon='close'
                  size='small'>
                  Close
                </Button>
              </div>
            </div>
            <iframe
              src={uploadIframeLink}
              width="100%"
              height="790px"
              style={{ border: '1px solid #ccc', borderRadius: '4px' }}
              title="Upload Insurance Documents"
            />
          </div>
        )}

        {/* Left side - Verification Results iFrame */}
        {showVerificationIframe && verificationResultLink && (
          <div style={{ 
            width: '600px', 
            flexShrink: 0,
            border: '1px solid #ccc', 
            borderRadius: '4px', 
            padding: '15px' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>Verification Results</h3>
              <Button 
                onClick={() => setShowVerificationIframe(false)}
                themeColor={'secondary'}
                icon='close'
                size='small'>
                Close
              </Button>
            </div>
            <iframe
              src={verificationResultLink}
              width="100%"
              height="790px"
              style={{ border: '1px solid #ccc', borderRadius: '4px' }}
              title="Insurance Verification Results"
            />
          </div>
        )}

        {/* Right side - Insurance cards */}
        <div className={styles.customerInsuranceCards} style={{ flex: 1 }}>
          {[...props.customerVehicleInsurances].map((item) => (
            item.isCpi ? (
              <CpiInsuranceCard key={item.id} data={item} onEdit={() => setEditItemId(item.id!)} onDelete={() => setDeleteItemId(item.id!)} />
            ) : (
              <VehicleInsuranceCard key={item.id} data={item} onEdit={() => setEditItemId(item.id!)} onDelete={() => setDeleteItemId(item.id!)} />
            )
          ))}
        </div>
      </div>

      {/* Insurance Form Dialog - used for create, edit, and pending insurance from Modives */}
      <InsuranceFormWindow
        visible={isCreating || hasValue(editItemId) || (showPendingInsuranceEditor && pendingInsurance !== null && props.customerVehicleInsurances.length === 0)}
        mode={(isCreating || showPendingInsuranceEditor) ? "create" : "edit"}
        initialData={editItemId
          ? {
              ...props.customerVehicleInsurances.find(i => i.id === editItemId)!,
              appCustomerId: props.appCustomerId,
              effectiveDate: (() => { const item = props.customerVehicleInsurances.find(i => i.id === editItemId); return item?.effectiveDate ? new Date(item.effectiveDate) : null; })(),
              expirationDate: (() => { const item = props.customerVehicleInsurances.find(i => i.id === editItemId); return item?.expirationDate ? new Date(item.expirationDate) : null; })(),
              cancellationDate: (() => { const item = props.customerVehicleInsurances.find(i => i.id === editItemId); return item?.cancellationDate ? new Date(item.cancellationDate) : null; })(),
              lastUpdate: (() => { const item = props.customerVehicleInsurances.find(i => i.id === editItemId); return item?.lastUpdate ? new Date(item.lastUpdate) : null; })(),
            }
          : (showPendingInsuranceEditor && pendingInsurance)
            ? { ...convertPendingInsuranceToVehicleInsurance(pendingInsurance, currentApp?.vehicle), appCustomerId: props.appCustomerId }
            : null
        }
        existingInsurance={props.customerVehicleInsurances.map(i => ({ ...i, appCustomerId: props.appCustomerId }))}
        insuranceAgents={insuranceAgents}
        appCustomerId={props.appCustomerId}
        vehicleStockNumber={currentApp?.vehicle?.stockNumber ?? ''}
        warningMessage={
          showPendingInsuranceEditor && pendingInsurance && currentApp?.vehicle?.vin
            && pendingInsurance.vin
            && pendingInsurance.vin.toUpperCase().trim() !== currentApp.vehicle.vin.toUpperCase().trim()
            ? 'The verified VIN does not match the vehicle on this application. Please verify the stock number.'
            : undefined
        }
        defaultCpiFactor={defaultCpiFactor}
        defaultCpiNextAccrueDate={defaultCpiNextAccrueDate}
        loanPayFrequencyLabel={loanPayFrequencyLabel}
        onSave={(data) => {
          if (hasValue(editItemId)) {
            handleEdit({ ...data, id: editItemId!, appCustomerId: props.appCustomerId });
          } else {
            handleCreate(data);
            setShowPendingInsuranceEditor(false);
          }
        }}
        onCancel={() => {
          setIsCreating(false);
          setEditItemId(null);
          setShowPendingInsuranceEditor(false);
        }}
      />

      {/* Dialogs rendered outside the flex layout */}
      {hasValue(deleteItemId) && (
          <Window width={400} height={200} title="Delete Insurance" onClose={() => setDeleteItemId(null)}>
            <p style={{ margin: 0 }}>Are you sure you want to delete this insurance record? This cannot be undone.</p>
            <WindowActionsBar>
              <Button onClick={() => setDeleteItemId(null)}>Cancel</Button>
              <Button themeColor={'error'} onClick={() => handleDelete()}>Delete</Button>
            </WindowActionsBar>
          </Window>
        )}
        {showDocUploadSendDialog && (
          <Window
            width={500}
            height={380}
            title="Send Document Upload Link"
            onClose={() => setShowDocUploadSendDialog(false)}
            themeColor='primary'
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '4px 0' }}>
              <div>
                <label style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>Send via</label>
                <RadioGroup
                  layout="horizontal"
                  data={[
                    { label: 'Email', value: 'email' },
                    { label: 'Text', value: 'text' },
                  ]}
                  value={docUploadSendMethod}
                  onChange={(e: RadioGroupChangeEvent) => setDocUploadSendMethod(e.value as 'email' | 'text')}
                />
              </div>
              {docUploadSendMethod === 'email' && (
                <Input
                  label="Email Address"
                  type="email"
                  value={docUploadEmail}
                  onChange={(e: InputChangeEvent) => setDocUploadEmail(e.value as string)}
                  style={{ width: '100%' }}
                />
              )}
              {docUploadSendMethod === 'text' && (
                <Input
                  label="Phone Number"
                  type="tel"
                  value={docUploadPhone}
                  onChange={(e: InputChangeEvent) => setDocUploadPhone(e.value as string)}
                  style={{ width: '100%' }}
                />
              )}
              <div style={{ fontSize: '14px', color: '#666', padding: '10px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                A link to upload insurance documents will be sent to the {docUploadSendMethod === 'email' ? 'email address' : 'phone number'} above.
              </div>
            </div>
            <WindowActionsBar>
              <Button onClick={() => setShowDocUploadSendDialog(false)}>Cancel</Button>
              <Button
                themeColor="primary"
                disabled={docUploadSendMethod === 'email' ? !hasValue(docUploadEmail) : !hasValue(docUploadPhone)}
                onClick={handleDocUploadSend}
              >
                Send
              </Button>
            </WindowActionsBar>
          </Window>
        )}
        {showApplicantSelectionDialog && (
          <Window 
            width={500} 
            height={290} 
            title={pendingDocUploadAfterVerification ? "Select Applicant for Insurance Verification" : (verificationStatus ? "Select Applicant for Document Upload" : "Select Applicant for Insurance Verification")}
            onClose={() => { setShowApplicantSelectionDialog(false); setPendingDocUploadAfterVerification(false); setDocUploadCustomerId(null); }}
            themeColor='primary'
          >
            <p style={{ margin: '0 0 15px 0' }}>
              {pendingDocUploadAfterVerification || !verificationStatus
                ? 'This application has multiple applicants. Please select which applicant\'s information to send for insurance verification:'
                : (docUploadMode === 'iframe'
                  ? 'Select which applicant to upload documents for:'
                  : 'Select which applicant to generate the document upload link for:')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[...(currentApp?.appCustomers ?? [])].sort((a, b) => (b.customerRole?.isPrimaryApplicant ? 1 : 0) - (a.customerRole?.isPrimaryApplicant ? 1 : 0)).map((appCustomer) => (
                <Button
                  key={appCustomer.id}
                  onClick={() => {
                    if (pendingDocUploadAfterVerification || !verificationStatus) {
                      setDocUploadCustomerId(appCustomer.customerId);
                      sendVerificationForCustomer(appCustomer.customerId);
                    } else {
                      setShowApplicantSelectionDialog(false);
                      proceedToDocumentUpload(appCustomer.customerId);
                    }
                  }}
                  themeColor='primary'
                  fillMode='outline'
                  style={{ justifyContent: 'flex-start', padding: '10px 15px' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 'bold' }}>
                      {appCustomer.customer?.firstName} {appCustomer.customer?.lastName}
                    </span>
                    <span style={{ fontSize: '0.85em', color: '#666' }}>
                      {appCustomer.customerRole?.isPrimaryApplicant ? 'Primary Applicant' : 'Co-Applicant'}
                      {appCustomer.customer?.customerEmails?.[0]?.emailAddress && 
                        ` • ${appCustomer.customer.customerEmails[0].emailAddress}`}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          </Window>
        )}
    </div>
  );
}

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return '$0.00';
  return `$${value.toFixed(2)}`;
};

function CpiInsuranceCard({ data, onEdit, onDelete }: {
  data: IAppCustomerVehicleInsurance;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card style={{ borderRadius: 8, padding: 16, maxWidth: 800, backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#2d3748' }}>CPI Insurance</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon="edit" fillMode="flat" onClick={onEdit} />
          <Button icon="trash" fillMode="flat" onClick={onDelete} />
        </div>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>Stock #:</span> {data.stockNumber || '-'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>CPI Factor:</span> {formatCurrency(data.cpiFactor)}
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>CPI Start Date:</span> {formatDate(data.cpiStartDate)}
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>CPI Next Accrue Date:</span> {formatDate(data.cpiNextAccrueDate)}
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>Semi 1 Day of Month:</span> {data.cpiSemi1DayOfMonth ?? 0}
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 'bold', marginRight: 8 }}>Semi 2 Day of Month:</span> {data.cpiSemi2DayOfMonth ?? 0}
            </div>
          </div>
        </div>
        {(data.memo || data.memo2) && (
          <div style={{ marginTop: 8 }}>
            {data.memo && <div style={{ marginBottom: 4 }}><span style={{ fontWeight: 'bold', marginRight: 8 }}>Memo:</span> {data.memo}</div>}
            {data.memo2 && <div><span style={{ fontWeight: 'bold', marginRight: 8 }}>Memo 2:</span> {data.memo2}</div>}
          </div>
        )}
      </CardBody>
    </Card>
  );
}